import { useCallback } from 'react';
import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { VStack } from '@cbhq/cds-web/layout';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import type { ErrorMetadata } from '@cbhq/error-vitals-web';
import { ErrorBoundary } from '@cbhq/error-vitals-web';

import { BasicErrorFallback } from '../components/ErrorBoundary/BasicErrorFallback';
import { NavigationContentSpinner } from '../components/Navigation';
import { useOrganization } from '../contexts/OrganizationContext';
import { ApprovalsPage } from '../pages/Approvals/pages/ApprovalsPage';
import { ROUTE_APPROVALS } from '../pages/Approvals/utils/constants';
import { ROUTE_TEAM } from '../pages/Team/utils/constants';
import { AppExperiments, useExperiments, useIsTreatment } from '../utils/experiments';

const APPROVALS_ERROR_BOUNDARY_DEFAULT_METADATA: ErrorMetadata = {
  context: 'error-boundary',
  severity: 'error',
  isHandled: true,
};

function ApprovalsRoutes(): ReactElement {
  const { isLoaded } = useExperiments();
  const isWorkspaceConsensusTreatment = useIsTreatment(AppExperiments.t0018_7b870e9f);
  const shouldShowApprovals = isWorkspaceConsensusTreatment;

  if (!isLoaded) {
    return <NavigationContentSpinner />;
  }

  if (!shouldShowApprovals) {
    return <Navigate to={ROUTE_TEAM} replace />;
  }

  return (
    <Routes>
      <Route index element={<ApprovalsPage />} />
      <Route path="*" element={<Navigate to={ROUTE_APPROVALS} replace />} />
    </Routes>
  );
}

export default function Approvals(): ReactElement {
  const { selectedOrganization } = useOrganization();
  const handleCatch = useCallback((error: Error) => {
    logEvent('approvals_management_error', {
      action: ActionType.measurement,
      componentType: ComponentType.page,
      error: error.message,
    });
  }, []);

  return (
    <VStack width="100%" height="100%">
      <ErrorBoundary
        // Remount the boundary when the org changes so a stale cached error
        // for the previous org doesn't keep the fallback stuck after switching.
        key={selectedOrganization?.organizationId}
        name="approvals-boundary"
        defaultMetadata={APPROVALS_ERROR_BOUNDARY_DEFAULT_METADATA}
        fallback={<BasicErrorFallback />}
        onCatch={handleCatch}
      >
        <ApprovalsRoutes />
      </ErrorBoundary>
    </VStack>
  );
}
